import React, { useState, useEffect } from 'react';
import { Button, Space, message, Input, Upload, Form } from 'antd';
import { PlusOutlined, SearchOutlined, DownloadOutlined, ReloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText, ProFormTextArea } from '@ant-design/pro-components';
import { renderDateTime, ActionButtons, ResizableTable } from './utils';

interface DownloadItem {
  id: number;
  file_name: string;
  file_url: string;
  file_desc: string;
  create_time: string;
}

export default function DownloadCenter() {
  const [items, setItems] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<DownloadItem | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();
  const [fileUrl, setFileUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/download-center', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setItems(resp.data || []);
      } else {
        message.error(resp?.error || '获取下载列表失败');
      }
    } catch (err) {
      message.error('获取下载列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setFileUrl('');
    setModalVisible(true);
  };

  const handleEdit = (record: DownloadItem) => {
    setEditingItem(record);
    form.setFieldsValue({
      file_name: record.file_name,
      file_desc: record.file_desc,
    });
    setFileUrl(record.file_url || '');
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/download-center/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchItems();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!fileUrl) {
        message.error('请上传文件');
        return;
      }
      
      const token = localStorage.getItem('admin_token');
      const url = editingItem 
        ? `/api/admin/download-center/${editingItem.id}`
        : '/api/admin/download-center';
      const method = editingItem ? 'PUT' : 'POST';

      const submitData = {
        ...values,
        file_url: fileUrl,
      };

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingItem ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchItems();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err: any) {
      if (err.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('操作失败');
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      }).then(r => r.json());

      if (resp && resp.ok) {
        setFileUrl(resp.url);
        message.success('上传成功');
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch (err) {
      message.error('上传失败');
    } finally {
      setUploading(false);
    }
    
    return false;
  };

  // 过滤数据
  const filteredData = items.filter(i => 
    i.file_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    i.file_desc?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<DownloadItem> = [
    {
      title: '文件名称',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '文件描述',
      dataIndex: 'file_desc',
      key: 'file_desc',
      ellipsis: true,
      render: (text) => <span style={{ color: '#6b7280' }}>{text || '-'}</span>
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: renderDateTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_, record) => (
        <ActionButtons
          onDownload={async () => {
            try {
              // 使用fetch获取文件blob
              const response = await fetch(record.file_url);
              const blob = await response.blob();
              
              // 创建blob URL并下载
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = record.file_name || 'download';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // 释放blob URL
              window.URL.revokeObjectURL(blobUrl);
            } catch (err) {
              message.error('下载失败，请稍后重试');
            }
          }}
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record.id)}
          deleteTitle="确定要删除这个文件吗？"
          showDownload={true}
        />
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <DownloadOutlined />
          </div>
          <div>
            <h2>下载中心</h2>
            <p>管理可下载文件</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索文件..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchItems}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增文件
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <ResizableTable
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <DownloadOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingItem ? '编辑文件' : '新增文件'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
        submitter={{
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="file_name"
            label="文件名称"
            placeholder="请输入文件名称"
            rules={[{ required: true, message: '请输入文件名称' }]}
          />
          
          <Form.Item label="上传文件" required>
            <Upload.Dragger
              name="file"
              showUploadList={false}
              beforeUpload={handleFileUpload}
              disabled={uploading}
            >
              {fileUrl ? (
                <div style={{ padding: '20px' }}>
                  <p style={{ color: '#52c41a', marginBottom: 8 }}>✓ 已上传文件</p>
                  <p style={{ color: '#999', fontSize: 12 }}>{fileUrl}</p>
                  <p style={{ color: '#3b82f6', marginTop: 8 }}>点击重新上传</p>
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#3b82f6', fontSize: 48 }} />
                  </p>
                  <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                  <p className="ant-upload-hint">支持各种格式文件</p>
                </div>
              )}
            </Upload.Dragger>
          </Form.Item>
          
          <ProFormTextArea
            name="file_desc"
            label="文件描述"
            placeholder="请输入文件描述"
            fieldProps={{ rows: 3 }}
          />
        </div>
      </ModalForm>
    </div>
  );
}

